import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonProgressBar,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {checkmarkCircleOutline} from "ionicons/icons";
import {useEmployeeClientId, useEmployeeLocations} from "app/employee/state/bootstrapEmployee/selectors";
import {getLocationText} from "app/employee/utils";
import {RhfIonInput} from "app/employee/rhf/RhfIonInput";
import {RhfIonTextarea} from "app/employee/rhf/RhfIonTextarea";
import {RhfTypeaheadMultiSelect} from "app/employee/rhf/RhfTypeaheadMultiSelect";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {MenuButton} from "app/employee/menu/MenuButton";
import {useDismiss} from "app/employee/incdoc/useDismiss";
import {useLoadingContext} from "app/employee/LoadingContext";
import {getUploadyDestination, query} from "app/gql/client";
import {
  type ControlCardReferenceInput,
  CreateTicketDocument,
  type LocationIdFragment,
  type RecipientUserFragment,
  RecipientUserType,
  SearchTicketRecipientsDocument,
  type TicketDetailFragment,
  UpdateTicketDocument,
  type UserFragment
} from "app/gql/graphqlSchema";
import {zLocationId, zNormalizedString} from "app/utils/validator";
import type {MaybeNil} from "app/utils/types";
import {useCallback, useEffect, useMemo} from "react";
import {ListHeaderErrorMessage} from "app/employee/rhf/GlobalErrorMessage";
import {TicketNumber} from "app/employee/ticket/TicketNumber";
import {parseErrorResult} from "app/utils/parseErrorResult";
import {linkEmplTickets} from "app/employee/core/employeeLinks";
import {fractionToPercent, isEqual, isNotBlank} from "app/utils/stdlib";
import {useQuery} from "app/employee/controlCard/hook/useQuery";
import {UnreachableCaseError} from "ts-essentials";
import {Uploady} from "@rpldy/uploady";
import {useAttachmentCollectionEditor} from "app/components/hooks/useAttachmentCollectionEditor";
import {AttachmentCollectionEdit} from "app/employee/att/AttachmentCollectionEdit";
import {UploadButton} from "app/employee/att/UploadButton";

const keyExtractor = (o: {key: string}) => o.key;
const textExtractor = (o: {text: string}) => o.text;
const optionMapper = <Value,>(o: MaybeNil<{value: Value}>) => o?.value;

const getRecipientId = (r: UserFragment): string => {
  switch (r.__typename) {
    case "Employee":
      return r.employeeId.employeeId;
    case "Manager":
      return r.managerId.managerId;
    case "BoUser":
      return r.id;
    default:
      throw new UnreachableCaseError(r);
  }
};

// Build the fuzzy-search haystack per recipient — name plus the fields the
// employee can type in the searchbar but that aren't rendered in the list.
const getRecipientSearchText = (r: RecipientUserFragment): string => {
  switch (r.__typename) {
    case "Employee":
      return [r.name, r.position?.name, ...r.locations.map(getLocationText)].filter(isNotBlank).join(" ");
    case "Manager":
      return [r.name, "Управител", ...r.locations.map(getLocationText)].filter(isNotBlank).join(" ");
    case "BoUser":
      return r.name;
    default:
      throw new UnreachableCaseError(r);
  }
};

const recipientOptionMapper = (o: MaybeNil<{value: string}>) => o?.value;

interface Props {
  ticket?: TicketDetailFragment;
  onCompleted?: () => void;
  controlCardReference?: ControlCardReferenceInput;
  defaultLocationId?: LocationIdFragment;
  recipientGroupKeys?: string[];
}

const AddEditTicketInner = ({
  ticket,
  onCompleted,
  controlCardReference,
  defaultLocationId,
  recipientGroupKeys
}: Props) => {
  const {t} = useTranslation();
  const locations = useEmployeeLocations();
  const clientId = useEmployeeClientId();
  const dismiss = useDismiss(linkEmplTickets());
  const {startLoading, finishLoading} = useLoadingContext();
  const isEdit = ticket !== undefined;

  const {showFileUpload, unifiedAttachments, uploadProgress, cleanup, collectOcrFiles, pendingAttachmentsForRemoval} =
    useAttachmentCollectionEditor(isEdit ? ticket.attachmentCollection : null);

  const {data: recipientsData, fetch: fetchRecipients} = useQuery(
    SearchTicketRecipientsDocument,
    (r) => r.searchTicketRecipients.results
  );

  useEffect(() => {
    fetchRecipients({
      predicate: {
        clientId,
        query: null,
        userTypes: [RecipientUserType.Employee, RecipientUserType.Manager],
        groupKeys: recipientGroupKeys ?? [],
        page: {number: 1, size: 1000, order: []}
      }
    });
  }, [clientId, fetchRecipients, recipientGroupKeys]);

  const recipientOptions = useMemo(
    () =>
      (recipientsData.value ?? []).map((r) => ({
        key: getRecipientId(r),
        value: getRecipientId(r),
        text: r.name,
        secondary: r.__typename === "Employee" ? (r.position?.name ?? null) : null,
        searchText: getRecipientSearchText(r),
        type: r.__typename
      })),
    [recipientsData.value]
  );

  const getRecipientGroup = useCallback(
    (o: (typeof recipientOptions)[number]): [number, string] => {
      switch (o.type) {
        case "Employee":
          return [1, t("ticket.add.group-employees")];
        case "Manager":
        case "BoUser":
          return [0, t("ticket.add.group-managers")];
        default:
          throw new UnreachableCaseError(o.type);
      }
    },
    [t]
  );

  const formSchema = useMemo(
    () =>
      z.object({
        locationIds: z.array(zLocationId).default([]),
        recipientIds: z.array(z.string()).min(1, t("ticket.add.recipients-required")),
        subject: zNormalizedString.pipe(z.string().min(1, t("g.required"))),
        content: zNormalizedString
      }),
    [t]
  );

  type FormData = z.infer<typeof formSchema>;

  const {
    control,
    handleSubmit,
    setError,
    formState: {errors, isSubmitting}
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: isEdit
      ? {
          locationIds: ticket.locations?.map((l) => l.id) ?? [],
          recipientIds: ticket.recipients.map((r) => getRecipientId(r.recipient)),
          subject: ticket.subject,
          content: ticket.content ?? ""
        }
      : {
          locationIds: defaultLocationId
            ? locations.filter((l) => isEqual(l.id, defaultLocationId)).map((l) => l.id)
            : [],
          subject: "",
          content: "",
          recipientIds: []
        }
  });

  const saveDisabled = !uploadProgress.completed || isSubmitting;

  const onSubmit = async (data: FormData) => {
    startLoading();
    try {
      if (isEdit) {
        const {stagedUploadIds} = collectOcrFiles();
        const result = await query(UpdateTicketDocument, {
          input: {
            id: {clientId, ticketId: ticket.id.ticketId},
            locationIds: data.locationIds.map((l) => l.locationId),
            subject: data.subject,
            content: data.content || null,
            recipientIds: data.recipientIds,
            stageUploadIds: stagedUploadIds,
            removedAttachmentIds: pendingAttachmentsForRemoval,
            controlCardReference: ticket.controlCardReference
              ? {
                  instanceId: ticket.controlCardReference.instance.instanceId,
                  rowInstanceId: ticket.controlCardReference.rowInstanceId,
                  columnInstanceId: ticket.controlCardReference.columnInstanceId
                }
              : null
          }
        });
        const updateResult = result.updateTicket;
        if (updateResult.__typename === "ErrorResult") {
          parseErrorResult(updateResult, setError);
          return;
        }
        cleanup();
        await dismiss();
        onCompleted?.();
      } else {
        const {stagedUploadIds} = collectOcrFiles();
        const result = await query(CreateTicketDocument, {
          input: {
            clientId,
            locationIds: data.locationIds.map((l) => l.locationId),
            subject: data.subject,
            content: data.content || null,
            recipientIds: data.recipientIds,
            stageUploadIds: stagedUploadIds,
            controlCardReference
          }
        });

        const createResult = result.createTicket;
        if (createResult.__typename === "ErrorResult") {
          parseErrorResult(createResult, setError);
          return;
        }

        cleanup();
        await dismiss();
        onCompleted?.();
      }
    } finally {
      finishLoading();
    }
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>
            {isEdit ? (
              <>
                {t("ticket.detail.title")} <TicketNumber ticketId={ticket.id} />
              </>
            ) : (
              t("ticket.add.title")
            )}
          </IonTitle>
          <MenuButton />
        </IonToolbar>
        <IonProgressBar type="determinate" value={uploadProgress.completed ? 1 : uploadProgress.progress} />
      </IonHeader>
      <IonContent>
        <IonList lines="full">
          <ListHeaderErrorMessage errors={errors} />
          <RhfTypeaheadMultiSelect
            groupExtractor={getRecipientGroup}
            keyExtractor={keyExtractor}
            optionMapper={recipientOptionMapper}
            options={recipientOptions}
            required
            rhf={{control, name: "recipientIds"}}
            searchPlaceholder={t("ticket.add.recipients-search-placeholder")}
            searchTextExtractor={(o) => o.searchText}
            secondaryTextExtractor={(o) => o.secondary}
            textExtractor={textExtractor}
            title={t("ticket.add.recipients-label")}
          />
          <IonItem lines="full">
            <RhfIonInput
              autocapitalize="on"
              label={t("ticket.add.subject")}
              labelPlacement="stacked"
              placeholder={t("ticket.add.subject-placeholder")}
              rhf={{control, name: "subject"}}
            />
          </IonItem>
          <IonItem lines="full">
            <RhfIonTextarea
              autocapitalize="on"
              label={t("ticket.add.content")}
              labelPlacement="stacked"
              placeholder={t("ticket.add.content-placeholder")}
              rhf={{control, name: "content"}}
              rows={4}
            />
          </IonItem>
          <IonItem lines="full">
            <IonLabel>
              <AttachmentCollectionEdit
                disabled={isSubmitting}
                showFileUpload={showFileUpload}
                unifiedAttachments={unifiedAttachments}
              />
            </IonLabel>
          </IonItem>
          <RhfTypeaheadMultiSelect
            keyExtractor={keyExtractor}
            optionMapper={optionMapper}
            options={locations.map((location) => ({
              key: location.id.locationId,
              value: location.id,
              text: getLocationText(location)
            }))}
            placeholder={t("ticket.add.location-placeholder")}
            rhf={{control, name: "locationIds"}}
            textExtractor={textExtractor}
            title={t("ticket.add.location")}
          />
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="primary">
            <UploadButton
              color="secondary"
              disabled={isSubmitting}
              fill="solid"
              showFileUpload={showFileUpload}
              unifiedAttachments={unifiedAttachments}
            />
            <IonButton color="primary" disabled={saveDisabled} fill="solid" onClick={handleSubmit(onSubmit)}>
              <IonIcon icon={checkmarkCircleOutline} slot="start" />
              {!uploadProgress.completed
                ? t("g.uploading", {
                    uploadProgress: fractionToPercent(uploadProgress.progress)
                  })
                : isEdit
                  ? t("ticket.edit.submit")
                  : t("ticket.add.submit")}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  );
};

export const AddEditTicket = (props: Props) => (
  <Uploady concurrent destination={getUploadyDestination()} maxConcurrent={2} method="POST" multiple>
    <AddEditTicketInner {...props} />
  </Uploady>
);
